import { z } from "zod";

/**
 * Fixed report order for the "Product selection" rationale rows; labels are
 * server-owned rather than model-authored, exactly as `EXEC_SUMMARY_SLOTS` is.
 */
export const PRODUCT_SELECTION_SLOTS = ["why_vendor", "why_fits_org"] as const;

export const ProductSelectionSlotSchema = z.enum(PRODUCT_SELECTION_SLOTS);
export type ProductSelectionSlot = z.infer<typeof ProductSelectionSlotSchema>;

export const PRODUCT_SELECTION_SLOT_LABELS: Record<
  ProductSelectionSlot,
  string
> = {
  why_vendor: "Why this vendor",
  why_fits_org: "Why it fits this organization",
};

/**
 * The **browser-verifiable** slice of what this prose is about — the same role
 * `ExecSummaryStructure` plays for the executive summary, and the same
 * discipline: **identifiers only, never values.**
 *
 * `packetHash` is the authoritative staleness signal, but it is derived from a
 * prompt packet the browser never receives (problem statement, workflows,
 * deployment facts, full product payloads), so the renderer cannot recompute it
 * and cannot use it to decide whether stored prose still describes the current
 * product step. This structure is the part of that same input the renderer
 * *does* hold: it is rebuilt from the committed read model on both sides and
 * compared as sets. A mismatch discards the **whole** artifact, never one row —
 * a product that changed under the prose can then never render.
 *
 * Scope, and why each:
 * - `selectedProductId` — the product this report models. If it changes, every
 *   rationale row is about a different vendor. `null` when the intake finished
 *   without a product (empty pool; `route_pool_gate` skipped selection).
 * - `metricIds` — the metrics the fit claims rest on; prose that grounds a fit
 *   in a metric the model no longer carries is unverifiable.
 *
 * Excluded on purpose: the option set the user was shown, which this prose no
 * longer speaks about — every row is about the chosen product, so a different
 * set of alternatives cannot falsify one. Also excluded: labels and vendor
 * names (a rename is not a structural change and would churn artifacts),
 * pricing, scores, and every other value.
 */
export const ProductSelectionStructureSchema = z.object({
  selectedProductId: z.string().nullable(),
  metricIds: z.array(z.string()),
});
export type ProductSelectionStructure = z.infer<
  typeof ProductSelectionStructureSchema
>;

function sameIdSet(left: readonly string[], right: readonly string[]): boolean {
  const seen = new Set(left);
  if (seen.size !== new Set(right).size) return false;
  return right.every((id) => seen.has(id));
}

/**
 * Order-independent equality across the whole structure — the counterpart of
 * `execSummaryStructureMatches`, and the only comparison the renderer makes
 * before it trusts stored prose.
 *
 * Both sides build their own copy from their own source (the service from the
 * prompt packet, the browser from the committed read model and the intake
 * projection), so nothing may rely on ordering; the selected product is a
 * single identity and is compared as one.
 */
export function productSelectionStructureMatches(
  left: ProductSelectionStructure,
  right: ProductSelectionStructure,
): boolean {
  return (
    left.selectedProductId === right.selectedProductId &&
    sameIdSet(left.metricIds, right.metricIds)
  );
}

/**
 * One rationale row. A slot that cannot be grounded in a supplied field is
 * omitted from the array entirely — `ReportVendorRationaleV2` renders only the
 * rows it is handed and never synthesizes placeholders.
 *
 * **`sourceId` is an identity, never an ordinal.** Citation numbers are not
 * durable anywhere in this stack (see `report-types.ts` `ReportSource`): a
 * regeneration renumbers the whole bibliography, so a persisted claim stores
 * the source's identity and the mapper resolves identity → number on every
 * build. Storing a number here would silently mis-cite after any change to the
 * source set.
 */
export const ProductSelectionRationaleRowSchema = z.object({
  slot: ProductSelectionSlotSchema,
  body: z.string(),
  sourceId: z.string().nullable(),
});
export type ProductSelectionRationaleRow = z.infer<
  typeof ProductSelectionRationaleRowSchema
>;

/**
 * The persisted `opportunities.product_selection` artifact.
 *
 * Its generation claim lives in `opportunities.product_selection_generation`
 * and reuses the executive summary's contract verbatim — `GenerationStageState`
 * plus an `attemptId` for the compare-and-set write, interpreted by
 * `execSummaryGenerationIsStale`. There is no second staleness protocol.
 */
export const ProductSelectionArtifactSchema = z.object({
  version: z.literal("v1"),
  generatedAt: z.string(),
  model: z.string(),
  /** Hash of the prompt packet — server-side staleness signal. */
  packetHash: z.string(),
  /** Browser-verifiable subject matter: what this prose is about. */
  structure: ProductSelectionStructureSchema,
  rationale: z.array(ProductSelectionRationaleRowSchema),
});
export type ProductSelectionArtifact = z.infer<
  typeof ProductSelectionArtifactSchema
>;
